import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { DateFilterTypesEnum, EntityType, PaymentService } from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import PaymentTopHeader from "../listings/top-listing-headers/payment-listing-top-header";
import PayableCard from "../listings/listing-cards/payables";
import SimpleSpinner from "../../components/loader";
import { UserLoginData } from "../../components/common/charts/Enums";
import DateHelper from "../../utils/DateHelper";
import { useLocation, useNavigate } from "react-router-dom";
import AddPaymentModal from "./AddPaymentModal";
import EditPayableModal from "./EditPayableModal";
import EditReceivableModal from "./EditReceivableModal";
import { usePermissions } from "../../hooks/usePermissions";
import { ALL_FILTER_KEY, resolveAllFilterCount, useAggregateFilterCounts } from "../../hooks/useAggregateFilterCounts";
import {
  getApiPaymentDueAccountOrganizationlistCounts,
} from "../../services/listFilterCounts.service";
import { PAYABLE_FILTER, RECEIVABLE_FILTER } from "./paymentListHelpers";

const DATE_FILTERS = {
  NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
  NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
  NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
};

interface PaymentListProps {
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: EntityType;
  className?: string;
  refreshTrigger?: number;
  onPaymentAdded?: () => void;
}

const PaymentList: React.FC<PaymentListProps> = ({
  isEntity,
  entityId,
  entityTypeId,
  className,
  refreshTrigger,
  onPaymentAdded,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id));
  const { canWrite } = usePermissions();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Payable");
  const [dateFilterType, setDateFilterType] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
  const [hasAnyPayments, setHasAnyPayments] = useState(false);

  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Property,
  });

  const [editModalConfig, setEditModalConfig] = useState<{
    isOpen: boolean;
    data: any | null;
  }>({
    isOpen: false,
    data: null,
  });

  const [editReceivableModalConfig, setEditReceivableModalConfig] = useState<{
    isOpen: boolean;
    data: any | null;
  }>({
    isOpen: false,
    data: null,
  });

  const requestVersionRef = useRef(0);

  const handleEditClick = useCallback((paymentData: any) => {
    setEditModalConfig({
      isOpen: true,
      data: paymentData,
    });
  }, []);

  const handleEditReceivableClick = useCallback((paymentData: any) => {
    setEditReceivableModalConfig({
      isOpen: true,
      data: paymentData,
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPaymentMode = params.get("paymentMode");
    const urlPaidFlow = params.get("paidFlowTypeId");

    if (urlPaymentMode && urlPaidFlow) {
      setActiveTab(urlPaymentMode === "Receivables" ? "Receivable" : "Payable");
    }
  }, [location.search]);

  const handleSort = useCallback((newDirection: "asc" | "desc") => {
    setSortDirection(newDirection);
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    const urlParams = new URLSearchParams(location.search);
    urlParams.set("paymentMode", tab === "Receivable" ? "Receivables" : "Payables");
    urlParams.set("paidFlowTypeId", tab === "Receivable" ? "2" : "1");
    navigate(`?${urlParams.toString()}`, { replace: true });
  }, [location.search, navigate]);

  const activeRowsFilter = activeTab === "Receivable" ? RECEIVABLE_FILTER : PAYABLE_FILTER;
  const scopeId = Number(entityId ?? orgId);

  const fetchPaymentRows = useCallback(async () => {
    requestVersionRef.current += 1;
    const currentRequestVersion = requestVersionRef.current;
    const isDateFiltered = [DATE_FILTERS.NEXT_30_DAYS, DATE_FILTERS.NEXT_3_MONTHS, DATE_FILTERS.NEXT_6_MONTHS].includes(
      dateFilterType as number,
    );

    setLoading(true);
    setRows([]);

    try {
      const sharedArgs = [
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        activeRowsFilter,
        sortDirection ? `DueDate ${sortDirection}` : undefined,
        isDateFiltered ? dateFilterType : undefined,
        isDateFiltered ? DateHelper.getClientDate() : undefined,
        undefined,
        undefined,
        isDateFiltered ? "DueDate" : undefined,
      ] as const;

      const response = isEntity && entityTypeId === EntityType.Property
        ? await apiHelper(
            PaymentService.getApiPaymentDueAccountPropertylist,
            { showNotification: false },
            scopeId,
            ...sharedArgs,
          )
        : isEntity && entityTypeId === EntityType.Unit
        ? await apiHelper(
            PaymentService.getApiPaymentDueAccountUnitlist,
            { showNotification: false },
            scopeId,
            ...sharedArgs,
          )
        : await apiHelper(
            PaymentService.getApiPaymentDueAccountOrganizationlist,
            { showNotification: false },
            orgId,
            ...sharedArgs,
          );

      if (currentRequestVersion !== requestVersionRef.current) {
        return;
      }

      setRows(response?.data?.success || []);
    } catch (err) {
      if (currentRequestVersion === requestVersionRef.current) {
        console.error("Error fetching payment data", err);
      }
    } finally {
      if (currentRequestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [activeRowsFilter, dateFilterType, entityTypeId, isEntity, orgId, scopeId, sortDirection]);

  const fetchCounts = useCallback(async () => {
    // Payments has no search box, so the date filter plays the role search normally would --
    // keep the pill counts consistent with it. The backend's dedicated counts pipeline
    // (BaseService.GetAggregateCountsOnly) only ever applies FullyQualifiedFilters and
    // silently ignores DateFilter.*, so it can only answer the *undated* question. When a
    // date filter is active we fall back to the same pageSize:1 row-list calls the
    // Property/Unit branch already uses (their DateFilter.* IS honored, since that's a real
    // row-fetch) and read recordCount instead.
    const isDateFiltered = [DATE_FILTERS.NEXT_30_DAYS, DATE_FILTERS.NEXT_3_MONTHS, DATE_FILTERS.NEXT_6_MONTHS].includes(
      dateFilterType as number,
    );
    const dateFilterTypeArg = isDateFiltered ? dateFilterType ?? undefined : undefined;
    const dateFilterClientDateArg = isDateFiltered ? DateHelper.getClientDate() : undefined;
    const dateFilterDateFieldArg = isDateFiltered ? "DueDate" : undefined;

    const fetchRowListCounts = async (paymentListApi: typeof PaymentService.getApiPaymentDueAccountOrganizationlist, id: number) => {
      const [payableRes, receivableRes] = await Promise.all([
        apiHelper(
          paymentListApi,
          { showNotification: false },
          id,
          1,
          1,
          undefined,
          undefined,
          undefined,
          PAYABLE_FILTER,
          undefined,
          dateFilterTypeArg,
          dateFilterClientDateArg,
          undefined,
          undefined,
          dateFilterDateFieldArg,
        ),
        apiHelper(
          paymentListApi,
          { showNotification: false },
          id,
          1,
          1,
          undefined,
          undefined,
          undefined,
          RECEIVABLE_FILTER,
          undefined,
          dateFilterTypeArg,
          dateFilterClientDateArg,
          undefined,
          undefined,
          dateFilterDateFieldArg,
        ),
      ]);

      return {
        Payable: Number(payableRes?.data?.recordCount ?? 0),
        Receivable: Number(receivableRes?.data?.recordCount ?? 0),
      };
    };

    if (isEntity && (entityTypeId === EntityType.Property || entityTypeId === EntityType.Unit)) {
      const paymentListApi = entityTypeId === EntityType.Property
        ? PaymentService.getApiPaymentDueAccountPropertylist
        : PaymentService.getApiPaymentDueAccountUnitlist;
      return fetchRowListCounts(paymentListApi, scopeId);
    }

    if (isDateFiltered) {
      return fetchRowListCounts(PaymentService.getApiPaymentDueAccountOrganizationlist, orgId);
    }

    const res = await apiHelper(
      getApiPaymentDueAccountOrganizationlistCounts,
      { showNotification: false },
      orgId,
      1,
      1,
      undefined,
      undefined,
      undefined,
      null,
      undefined,
    );
    return res?.data?.aggregateValues ?? undefined;
  }, [dateFilterType, entityTypeId, isEntity, orgId, scopeId]);

  const {
    aggregateValues: paymentCounts,
    baselineAggregateValues,
    refreshCounts,
    getVisibleOptions,
  } = useAggregateFilterCounts({
    fetchCounts,
    searchOnlyFilter: null,
    scopeKey: isEntity ? `payments:${entityTypeId}:${scopeId}` : `payments:org:${orgId}`,
  });

  const totalPaymentsCount = useMemo(
    () => resolveAllFilterCount(paymentCounts),
    [paymentCounts],
  );

  const filterOptions = useMemo(
    () =>
      getVisibleOptions([
        { title: ALL_FILTER_KEY, count: totalPaymentsCount },
        { title: "Payable" },
        { title: "Receivable" },
      ]).filter((option) => option.title !== ALL_FILTER_KEY),
    [getVisibleOptions, totalPaymentsCount],
  );

  useEffect(() => {
    if (!filterOptions.length) return;

    const activeFilterStillVisible = filterOptions.some((option) => option.title === activeTab);
    if (!activeFilterStillVisible) {
      handleTabChange(filterOptions[0].title);
    }
  }, [activeTab, filterOptions, handleTabChange]);

  useEffect(() => {
    // Tolerate a failed/in-flight counts fetch (apiHelper swallows the error) by also
    // trusting the row-list's own row count, so a single failing counts call can't hide the
    // whole page behind the empty state while rows actually loaded fine.
    setHasAnyPayments(resolveAllFilterCount(baselineAggregateValues) > 0 || rows.length > 0);
  }, [baselineAggregateValues, rows.length]);

  // `hasAnyPayments` is used below to distinguish "this org has never had a payment" (the
  // true empty state, safe to hide the header entirely) from "the current filter matched
  // nothing". Because `baselineAggregateValues` is itself date-filtered whenever a date
  // filter is active (see the `fetchCounts` comment above), selecting e.g. "Next 30 Days"
  // with zero matching payments used to make `hasAnyPayments` false and unmount the entire
  // header -- including the only control (the date-filter dropdown) that could clear the
  // filter, trapping the user in a false "no payments" empty state. A date filter counts as
  // an active filter, so keep the header mounted whenever one is selected regardless of the
  // (possibly zero) filtered count. `shouldShowHeader` (not `hasAnyPayments`) is also passed
  // down as `PayableCard`'s `hasAnyPayments` prop below, so its own `rows.length === 0` empty
  // state correctly renders "No Payments Found" rather than "Add Your First Payment" in this
  // same scenario -- the org DOES have payments, the active filter just matched none of them.
  const isDateFilterActive = dateFilterType !== null;
  const shouldShowHeader = hasAnyPayments || isDateFilterActive;

  useEffect(() => {
    fetchPaymentRows();
  }, [fetchPaymentRows]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchPaymentRows();
      refreshCounts();
    }
  }, [fetchPaymentRows, refreshCounts, refreshTrigger]);

  const MENU_ITEMS = useMemo(
    () => [
      {
        label: "All Payments",
        action: () => {
          setDateFilterType(null);
          setSortDirection(null);
        },
      },
      {
        label: "Next 30 Days",
        action: () => setDateFilterType(DATE_FILTERS.NEXT_30_DAYS),
      },
      {
        label: "Next 3 months",
        action: () => setDateFilterType(DATE_FILTERS.NEXT_3_MONTHS),
      },
      {
        label: "Next 6 months",
        action: () => setDateFilterType(DATE_FILTERS.NEXT_6_MONTHS),
        isDivider: false,
        isDestructive: false,
      },
    ],
    [],
  );

  return (
    <div className="content-wrapper container-fluid">
      {shouldShowHeader && (
        <PaymentTopHeader
          className={className}
          PageHeading="Payments"
          Count={filterOptions.reduce((sum, option) => sum + option.count, 0)}
          ButtonName="Add Payment"
          handleTabChange={handleTabChange}
          filterOptions={filterOptions}
          activeTab={activeTab}
          menuItems={MENU_ITEMS}
          handleSort={handleSort}
          currentSortDirection={sortDirection || "asc"}
          disableTabs={loading}
          showButton={canWrite("Payments")}
          onClick={() => isEntity
            ? setPaymentModalConfig({
                isOpen: true,
                entityId: Number(entityId),
                entityType: entityTypeId,
              })
            : navigate("/payment/create")}
        />
      )}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 h-100 w-100">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <PayableCard
          id="Payments"
          className={className}
          isFooter={true}
          onRefresh={() => {
            // PayableCard's row-level actions (Delete, Waive Payment) call this after
            // refreshing their own row list -- the pill/dropdown counts are a separate
            // fetch (useAggregateFilterCounts) and must be refreshed here too, or they
            // go stale (e.g. a waived Receivable payment keeps showing in its old pill).
            refreshCounts();
            if (onPaymentAdded) onPaymentAdded();
          }}
          dataHookState={{
            rows,
            loading,
            fetchData: fetchPaymentRows,
          }}
          onEditPayableClick={handleEditClick}
          onEditReceivableClick={handleEditReceivableClick}
          // NOT `hasAnyPayments`: `PayableCard` uses this flag to choose between "No Payments
          // Found" (heading only) and "Add Your First Payment" (heading + Add button) when its
          // own row list is empty. `hasAnyPayments` alone would say "Add Your First Payment"
          // (implying the org has never had a payment) even when a date filter simply matched
          // zero rows for an org that has payments elsewhere -- `shouldShowHeader` is exactly
          // the "is a filter active OR does the org genuinely have payments" condition that
          // distinguishes the two cases correctly.
          hasAnyPayments={shouldShowHeader}
          isEntity={isEntity}
          entityId={entityId}
          entityTypeId={entityTypeId}
          onAddClick={() => isEntity
            ? setPaymentModalConfig({
                isOpen: true,
                entityId: Number(entityId),
                entityType: entityTypeId,
              })
            : navigate("/payment/create")}
        />
      )}

      {paymentModalConfig.isOpen && (
        <AddPaymentModal
          isOpen={paymentModalConfig.isOpen}
          onClose={() => {
            setPaymentModalConfig((prev) => ({ ...prev, isOpen: false }));
            fetchPaymentRows();
            refreshCounts();
            if (onPaymentAdded) onPaymentAdded();
          }}
          entityIdProp={paymentModalConfig.entityId || undefined}
          entityTypeIdProp={paymentModalConfig.entityType}
          defaultPaymentMode={entityTypeId === EntityType.Tenant ? "2" : "1"}
        />
      )}

      {editModalConfig.isOpen && (
        <EditPayableModal
          isOpen={editModalConfig.isOpen}
          onClose={() => setEditModalConfig({ isOpen: false, data: null })}
          data={editModalConfig.data}
          onSuccess={() => {
            fetchPaymentRows();
            refreshCounts();
            if (onPaymentAdded) onPaymentAdded();
          }}
        />
      )}

      {editReceivableModalConfig.isOpen && (
        <EditReceivableModal
          isOpen={editReceivableModalConfig.isOpen}
          onClose={() => setEditReceivableModalConfig({ isOpen: false, data: null })}
          data={editReceivableModalConfig.data}
          onSuccess={() => {
            fetchPaymentRows();
            refreshCounts();
          }}
        />
      )}
    </div>
  );
};

export default PaymentList;
